<script>
   /*************************************************************/
   /* AppPlots component for Test tab                           */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';
   import { getCI } from '@mdatools-apps/shared/utils';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import plot components
   import PlotAcceptance from '@mdatools-apps/shared/plots/PlotAcceptance.svelte';
   import PlotDistances from '@mdatools-apps/shared/plots/PlotDistances.svelte';
   import PlotExtremes from '@mdatools-apps/shared/plots/PlotExtremes.svelte';
   import PlotAliens from '@mdatools-apps/shared/plots/PlotAliens.svelte';
   import PlotClassStat from '@mdatools-apps/shared/plots/PlotClassStat.svelte';
   import PlotScores from '@mdatools-apps/shared/plots/PlotScores.svelte';
   import PlotSelectivity from '@mdatools-apps/shared/plots/PlotSelectivity.svelte';

   // import plot controls components
   import { CAT_COLORS } from '@mdatools-apps/shared/plots/Defaults.js';
   import { getPlotAcceptanceControls, getPlotDistancesControls, getPlotExtremesControls, getPlotAliensControls,
            getPlotClassStatControls, getPlotScoresControls, getPlotSelectivityControls } from '@mdatools-apps/shared/plots/Controls.svelte.js';

   let {
      model,
      simca,
      testset,
      testres,
      markOutlier,
      showContributions
   } = $props();



   // because most of the plot components expect JSON with results
   // assuming there is more than one result object we create it
   // same for the data
   const res = {'test': testres.pca};
   const simcares = {'test': testres.simca};
   const data = {'test': testset};

   // this is derived as simca results can change without reloading the component
   const classres = $derived({'test': testres.simca});

   // colors for acceptance plot
   const classColmapMembers = {'regular' : CAT_COLORS.regular, 'extreme': CAT_COLORS.extreme, 'outlier': CAT_COLORS.outlier};
   const classColmapStrangers = {'alien' : CAT_COLORS.alien, 'external': CAT_COLORS.external};

   // options for acceptance plot
   const acceptanceOptions = $derived.by(() => {
      const res = testres.simca.v[simca.ncomp - 1];
      return {
         'members' : {
            classColmap: classColmapMembers,
            labels: [`regular: ${res.rolsNum.regular}`, `extreme: ${res.rolsNum.extreme}`, `outliers: ${res.rolsNum.outlier}`],
            values: res.rols,
            ind: testres.simca.ind.members
         },
         'strangers': {
            classColmap: classColmapStrangers,
            labels: [`aliens: ${res.rolsNum.alien}`, `external: ${res.rolsNum.external}`],
            values: res.rols,
            ind: testres.simca.ind.strangers
         }
      }
   });

   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived.by(() => {
      if (testres.simca.nMembers > 0 && testres.simca.nStrangers > 0) {
         return {
            acceptance: {label: 'Acceptance', scope: 'objects', component: PlotAcceptance, buttons: buttons2},
            distances: {label: 'Distances', scope: 'objects', component: PlotDistances, buttons: buttons2},
            extremes: {label: 'Extremes', scope: 'stats', component: PlotExtremes},
            aliens: {label: 'Aliens', scope: 'stats', component: PlotAliens},
            foms: {label: 'FoMs', scope: 'stats', component: PlotClassStat},
            sel: {label: 'Selectivity', scope: 'stats', component: PlotSelectivity},
            scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
         }
      }

      if (testres.simca.nMembers > 0) {
         return {
            acceptance: {label: 'Acceptance', scope: 'objects', component: PlotAcceptance, buttons: buttons2},
            distances: {label: 'Distances', scope: 'objects', component: PlotDistances, buttons: buttons2},
            extremes: {label: 'Extremes', scope: 'stats', component: PlotExtremes},
            foms: {label: 'FoMs', scope: 'stats', component: PlotClassStat},
            scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
         }
      }

      return {
         acceptance: {label: 'Acceptance', scope: 'objects', component: PlotAcceptance, buttons: buttons2},
         distances: {label: 'Distances', scope: 'objects', component: PlotDistances, buttons: buttons2},
         aliens: {label: 'Aliens', scope: 'stats', component: PlotAliens},
         foms: {label: 'FoMs', scope: 'stats', component: PlotClassStat},
         sel: {label: 'Selectivity', scope: 'stats', component: PlotSelectivity},
         scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
      }
   });

   // JSON with plots controls (as Widget properties). the rules below are attached once and nothing
   // changes this object afterwards, so it is a plain one rather than '$state'. 'untrack' marks the
   // initializer as a deliberate capture of what is on screen at mount - here it changes nothing,
   // and it is written this way to match the Predict tab, where a prop is read directly
   const controls = untrack(() => ({
      acceptance: getPlotAcceptanceControls(simcares),
      distances: getPlotDistancesControls(res),
      extremes: getPlotExtremesControls(['test']),
      aliens: getPlotAliensControls(['test']),
      foms: getPlotClassStatControls(simcares),
      sel: getPlotSelectivityControls(['test']),
      scores: getPlotScoresControls(res),
   }))

   // the factory marks these two 'hidden', which is where they start and not something the data
   // rules out - nothing here makes them impossible, they simply wait for the right selection.
   // 'hidden' is OR-ed with the rules below and would keep them hidden for good, so it is cleared
   // and their visibility is left entirely to 'hiddenWhen'
   controls.scores.colorByVar.hidden = false;
   controls.scores.confEllipse.hidden = false;

   // which controls do not apply to what the user has currently selected. the factories above set
   // 'hidden' for what the data rules out, and Widget combines the two with OR, so a rule here can
   // never bring back a control the data has excluded
   controls.scores.comp1.hiddenWhen       = (p) => p.plotType !== 'p';
   controls.scores.showEllipse.hiddenWhen = (p) => p.plotType !== 'p';
   controls.scores.colorBy.hiddenWhen     = (p) => p.plotType === 'l' || p.plotType === 'b';
   controls.scores.colorByVar.hiddenWhen  = (p) => p.colorBy !== 'variable';
   controls.scores.confEllipse.hiddenWhen = (p) => !(p.colorBy === 'groups' && p.plotType === 'p');

   // JSON with currently selected plots parameters
   let params = $state({
      acceptance: getDefaults(controls.acceptance),
      distances: getDefaults(controls.distances),
      extremes: getDefaults(controls.extremes),
      aliens: getDefaults(controls.aliens),
      foms: getDefaults(controls.foms),
      sel: getDefaults(controls.sel),
      scores: getDefaults(controls.scores),
   });

   // add confidence interval values for sensitivity plot
   $effect(() => {
      const ci = {};
      if (testres.simca.nMembers > 0) {
         ci['sens'] = getCI(testres.simca.nMembers, simca.alpha);
      }
      params.foms.ci = ci;
   });

   // add additional fixed parameters for the acceptance plot
   $effect(() => {
      params.acceptance['ncomp'] = simca.ncomp;
      params.acceptance['title'] = `Acceptance (A = ${simca.ncomp})`;
      params.acceptance['limType'] = simca.limType;
      params.acceptance['alpha'] = simca.alpha;
      params.acceptance['gamma'] = simca.gamma;
   });

   // if limType or result objects changed make updates to acceptance plot fixed parameters
   $effect(() => {
      if (acceptanceOptions && params.acceptance.subset) {
         params.acceptance['colorByObj'] = params.acceptance.subset !== 'all' ? acceptanceOptions[params.acceptance.subset] : null;
      }
   });

   // the subset selector owns 'colorBy' only while it is on screen. when there are no members it is
   // hidden and a 'color by' selector takes its place, so without this guard every choice made in
   // that selector was overwritten here on the next run
   $effect(() => {
      if (!controls.acceptance.subset.hidden) {
         params.acceptance['colorBy'] = params.acceptance.subset !== 'all' ? 'roles' : 'classes';
      }
   });

   // parameters whose control has gone away are reset, because the plot reads them whatever is
   // shown: 'col1' is computed from 'colorBy' for every plot type, and 'getLim' widens the axes
   // from the stored ellipse ranges whenever 'showEllipse' or 'confEllipse' say so, neither of
   // them re-checking the plot type. the other hidden parameters are read behind the same
   // condition which hides them, so they can keep their value
   $effect(() => {
      const p = params.scores;
      if (controls.scores.showEllipse.hiddenWhen(p)) p.showEllipse = false;
      if (controls.scores.colorBy.hiddenWhen(p))     p.colorBy = 'none';
      if (controls.scores.confEllipse.hiddenWhen(p)) p.confEllipse = 'none';
   });

   // combine main elements to be shared with all plots together
   const props = $derived({res, classres, model, simca, data, ncomp: simca.ncomp});


</script>

{#snippet buttons1(sel)}
   <Container label="Selected">
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}

{#snippet buttons2(sel)}
   <Container label="Selected">
      <Button onclick={() => showContributions(sel)} text="plot" />
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}

{#if simca && testres}
<PlotsRouter {plots} {controls} {props} bind:params={params} />
{/if}